import React, { useState } from "react";

function About() {
  const [flipped, setFlipped] = useState({});

  const setFlip = (id, state) => {
    setFlipped((prev) => ({ ...prev, [id]: state }));
  };

  const values = [
    {
      id: 1,
      icon: "🔒",
      title: "Security First",
      short: "Bank-grade protection",
      detail:
        "End-to-end encryption and multi-layer protection safeguard every transaction and user detail.",
    },
    {
      id: 2,
      icon: "💡",
      title: "Innovation",
      short: "Fintech at its finest",
      detail:
        "We constantly adopt cutting-edge fintech technologies to improve speed, safety, and reliability.",
    },
    {
      id: 3,
      icon: "🤝",
      title: "Trust",
      short: "Built on transparency",
      detail:
        "We believe trust is earned through honesty, reliability, and open communication with our users.",
    },
    {
      id: 4,
      icon: "⚡",
      title: "Speed",
      short: "Instant results",
      detail:
        "Experience lightning-fast transactions and settlements with our optimized infrastructure.",
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-blue-600 to-accent text-white py-20 md:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 font-poppins">
            About ZINDI
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Revolutionizing digital payments in Pakistan through innovation and
            trust
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-poppins">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At ZINDI, we're on a mission to transform Pakistan's digital
                payment landscape. We believe in bridging the gap between
                traditional local payment methods and modern global financial
                systems.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                By combining the reliability of Easypaisa, JazzCash, and bank
                transfers with the efficiency of cryptocurrency settlements,
                we're creating a payment ecosystem that serves both local
                merchants and international businesses.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl p-12 text-center">
              <div className="text-6xl mb-6">🎯</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-gray-600">
                To become Pakistan's most trusted and innovative payment
                gateway, empowering businesses with seamless, secure, and fast
                payment solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-poppins">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div
                key={value.id}
                className="relative h-80 preserve-3d cursor-pointer group"
                onClick={() => setFlip(value.id, !flipped[value.id])}
                onMouseEnter={() =>
                  !window.matchMedia("(pointer: coarse)").matches &&
                  setFlip(value.id, true)
                }
                onMouseLeave={() =>
                  !window.matchMedia("(pointer: coarse)").matches &&
                  setFlip(value.id, false)
                }
              >
                {/* Flip Container */}
                <div
                  className={`absolute inset-0 w-full h-full transition-transform duration-700 transform-gpu ${
                    flipped[value.id] ? "rotate-y-180" : "rotate-y-0"
                  }`}
                  style={{
                    transformStyle: "preserve-3d",
                    transitionTimingFunction: "cubic-bezier(0.4, 0.2, 0.2, 1)", // smooth like reference
                  }}
                >
                  {/* Front */}
                  <div className="absolute inset-0 backface-hidden bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center text-center hover:shadow-2xl transition-shadow">
                    <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600">{value.short}</p>
                    <p className="mt-4 text-xs text-primary font-semibold">
                      {window.matchMedia("(pointer: coarse)").matches
                        ? "Tap"
                        : "Hover"}{" "}
                      to see details →
                    </p>
                  </div>

                  {/* Back */}
                  <div className="absolute inset-0 backface-hidden rotate-y-180 bg-gradient-to-br from-primary to-accent rounded-2xl shadow-2xl p-8 flex flex-col justify-center text-white text-center">
                    <div className="text-4xl mb-4">{value.icon}</div>
                    <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                    <p className="text-sm leading-relaxed">{value.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CSS for 3D Flip */}
        <style jsx>{`
          .preserve-3d {
            transform-style: preserve-3d;
          }
          .backface-hidden {
            backface-visibility: hidden;
          }
          .rotate-y-0 {
            transform: rotateY(0deg);
          }
          .rotate-y-180 {
            transform: rotateY(180deg);
          }
        `}</style>
      </section>

      {/* Technology Stack */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-poppins">
              Powered by Advanced Technology
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Built on robust infrastructure to ensure reliability and
              performance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center text-4xl">
                ⚙️
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                TecPay Backend
              </h3>
              <p className="text-gray-600">
                Enterprise-grade payment processing infrastructure with 99.9%
                uptime
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-violet-50 to-white hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-accent to-purple-600 rounded-2xl flex items-center justify-center text-4xl">
                💱
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Binance Integration
              </h3>
              <p className="text-gray-600">
                Real-time exchange rates ensuring transparent and fair pricing
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center text-4xl">
                🌐
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                USDT (TRC20)
              </h3>
              <p className="text-gray-600">
                Fast and secure cryptocurrency settlements for global
                transactions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-poppins">
              Built by Experts
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our team combines deep expertise in fintech, blockchain, and local
              payment systems
            </p>
          </div>

          <div className="bg-white rounded-2xl p-12 shadow-xl max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-primary mb-2">10+</div>
                <p className="text-gray-600 font-medium">Years Experience</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-primary mb-2">50+</div>
                <p className="text-gray-600 font-medium">Team Members</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-primary mb-2">24/7</div>
                <p className="text-gray-600 font-medium">Support Available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-5">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-poppins text-center">
          Our Partners
        </h2>
        <div className="relative w-full overflow-hidden">
          <div className="flex space-x-16">
            {/* First set of logos */}
            <div className="flex space-x-16 animate-scroll">
              {[
                "/partners/sadapay.png",
                "/partners/easypaisa.png",
                "/partners/goloot.png",
                "/partners/jazzcash.png",

                "/partners/meezan.png",
                "/partners/nayapay.png",

                "/partners/scb.png",

                "/partners/zindagi.png",
              ].map((img, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 w-32 h-20 flex items-center justify-center"
                >
                  <img
                    src={img}
                    alt={`Partner Logo ${idx + 1}`}
                    className="max-h-12 object-contain mix-blend-multiply"
                  />
                </div>
              ))}
            </div>
            {/* Duplicate set for seamless loop */}
            <div className="flex space-x-16 animate-scroll">
              {[
                "/partners/sadapay.png",
                "/partners/easypaisa.png",
                "/partners/goloot.png",
                "/partners/jazzcash.png",

                "/partners/meezan.png",
                "/partners/nayapay.png",

                "/partners/scb.png",

                "/partners/zindagi.png",
              ].map((img, idx) => (
                <div
                  key={`duplicate-${idx}`}
                  className="flex-shrink-0 w-32 h-20 flex items-center justify-center"
                >
                  <img
                    src={img}
                    alt={`Partner Logo ${idx + 1}`}
                    className="max-h-12 object-contain mix-blend-multiply"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-100%);
            }
          }

          .animate-scroll {
            animation: scroll 40s linear infinite;
          }

          .animate-scroll:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-primary via-blue-600 to-accent text-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-poppins">
            Ready to Transform Your Payments?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses already using ZINDI for their payment
            needs
          </p>
          <button className="bg-white text-primary hover:bg-blue-50 px-10 py-5 rounded-xl text-lg font-semibold transition-all transform hover:scale-105 shadow-2xl">
            Get Started Today
          </button>
        </div>
      </section>
    </div>
  );
}

export default About;
